import { useMediaQuery } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useMemo, useRef, useState } from 'react';
import {
  Location,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import RequiredNoteConfirmationDialog from '../../../components/RequiredNoteConfirmationDialog';
import { SideActionItem } from '../../../components/SideActionsList';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  InvoiceInfo,
  InvoiceStatus,
  isInvoiceInfo,
} from '../../../types/invoices';
import { StatusIcon } from '../../../utils/icon';
import InvoiceTemplateActions from './InvoiceTemplateActions';
import StatusBadge from './StatusBadge';
import { InvoiceTemplateContext } from './types';
import { EntityStatus } from '../../../types/core';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const InvoiceTemplate = () => {
  const { t } = useLanguage();
  const { invoiceId: invoiceIdQueryParam } = useParams();
  const {
    getInvoiceJourneysByContractId,
    getInvoiceById,
    getInvoiceJourneysByInvoiceId,
    approveInvoice,
    rejectInvoice,
    getInvoiceRejectionNotes,
  } = useIotsApi();
  const { isOperator } = useCurrentUser();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery('(min-width:768px)');

  const [displayedInvoiceName, setDisplayedInvoiceName] = useState<string>('');

  const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);

  const [invoiceIdToReject, setInvoiceIdToReject] = useState<string | null>(
    null,
  );

  const submitNewInvoiceHandler = useRef(() => {});

  const invoiceId = useMemo(() => {
    return typeof invoiceIdQueryParam !== 'string' ||
      ['create'].includes(invoiceIdQueryParam.toLocaleLowerCase())
      ? null
      : invoiceIdQueryParam;
  }, [invoiceIdQueryParam]);

  const {
    data: invoice = null,
    isFetching: isFetchingInvoice,
    error,
    refetch,
  } = useQuery({
    queryKey: ['invoices', invoiceId ?? ''],
    queryFn: () => {
      if (!invoiceId) return null;
      return getInvoiceById(invoiceId);
    },
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const location = useLocation();

  const invoiceInfo = useMemo(() => {
    if (invoiceId) {
      if (invoice) {
        return {
          contractId: invoice.contractId,
          month: invoice.month,
          year: invoice.year,
        } as InvoiceInfo;
      }
    } else if (isInvoiceInfo(location.state)) {
      // Getting data from the location state
      const { state } = location as Location<InvoiceInfo>;
      return state;
    }

    return null;
  }, [invoice, invoiceId, location]);

  const {
    data: journeys = [],
    isFetching: isFetchingJourneys,
    isLoading: isLoadingJourneys,
    error: journeysError,
    refetch: refetchJourneys,
  } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      'journeys',
      'invoice',
      invoiceId ? 'id' : 'date',
      invoiceId ??
        [
          invoiceInfo?.contractId ?? '',
          invoiceInfo?.year ?? '',
          invoiceInfo?.month ?? '',
        ].join('-'),
    ],
    queryFn: () =>
      invoiceId
        ? getInvoiceJourneysByInvoiceId(invoiceId)
        : getInvoiceJourneysByContractId(
            invoiceInfo?.contractId ?? '',
            invoiceInfo?.year ?? 0,
            invoiceInfo?.month ?? 0,
          ),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: Boolean(invoiceInfo),
  });

  const {
    data: invoiceRejectionNotes = [],
    isLoading: isLoadingRejectionNotes,
    isFetching: isFetchingRejectionNotes,
    error: rejectionNotesError,
    refetch: refetchRejectionNotes,
  } = useQuery({
    queryKey: ['invoices', invoice?.id ?? '', 'rejectionNotes'],
    queryFn: () => getInvoiceRejectionNotes(invoice?.id ?? ''),
    enabled: invoice != null,
    // Default ordering descending by date
    select: notes =>
      // As the date is in ISO format, literal string comparison will serve (YYYY-MM-DD...)
      [...notes].sort((a, b) => b.creationDate.localeCompare(a.creationDate)),
  });

  useQueryErrorNotifier([error, journeysError]);

  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();

  const approvalMutation = useMutation({
    mutationFn: () => approveInvoice(invoiceId ?? ''),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.invoice-approval-success'),
      });
      void refetch();
      void refetchJourneys();
      void refetchRejectionNotes();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.invoice-approval-error')),
      });
      approvalMutation.reset();
    },
  });

  const rejectionMutation = useMutation({
    mutationFn: (note: string) => rejectInvoice(invoiceIdToReject ?? '', note),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.invoice-rejection-success'),
      });
      void refetch();
      void refetchJourneys();
      void refetchRejectionNotes();
      setInvoiceIdToReject(null);
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.invoice-rejection-error')),
      });
      rejectionMutation.reset();
    },
  });

  const contextValue = useMemo<InvoiceTemplateContext>(
    () => ({
      invoiceId,
      invoice,
      invoiceInfo,
      isFetching: isFetchingInvoice,
      refetch: () => void refetch(),
      onNewInvoiceFormStateChange: (newInvoiceName, canBeSubmitted, submit) => {
        setDisplayedInvoiceName(newInvoiceName);
        setIsSubmitEnabled(canBeSubmitted);
        submitNewInvoiceHandler.current = submit;
      },
      journeysManagement: {
        journeys,
        isFetching: !invoiceInfo || isFetchingJourneys,
        isLoading: !invoiceInfo || isLoadingJourneys,
        refetch: () => void refetchJourneys(),
      },
      rejectionNotesManagement: {
        rejectionNotes: invoiceRejectionNotes,
        isLoading: isLoadingRejectionNotes,
        error: rejectionNotesError,
      },
    }),
    [
      invoiceId,
      invoice,
      invoiceInfo,
      isFetchingInvoice,
      journeys,
      isLoadingJourneys,
      isFetchingJourneys,
      invoiceRejectionNotes,
      isLoadingRejectionNotes,
      rejectionNotesError,
      refetch,
      refetchJourneys,
    ],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    const baseUrl = `/admin/invoices/${invoiceId ?? 'create'}`;
    const invoiceUrl = `${baseUrl}/invoice`;
    return [
      {
        id: 'journeys',
        label: t('content.journeys'),
        statusIcon:
          /* The journey page status depends on the current status of the Invoice:
        - Rejected: Always ok
        - Approved: All journeys should have "Invoiced" status
        - Pending or in creation: All journeys shoud have "Submitted" status */
          invoice?.status === InvoiceStatus.Rejected ||
          (journeys.length > 0 &&
            !journeys.some(
              journey =>
                journey.entityStatus !==
                (invoice?.status === InvoiceStatus.Approved
                  ? EntityStatus.Invoiced
                  : EntityStatus.Submitted),
            ))
            ? StatusIcon.Completed
            : journeys.length === 0 && isFetchingJourneys
              ? StatusIcon.Pending
              : StatusIcon.Warning,
        url: baseUrl,
        onClick: event => {
          if (!invoiceId) {
            // For a placeholder, the location state must be preserved
            event.preventDefault();
            void navigate(baseUrl, {
              state: location.state as InvoiceInfo,
            });
          }
        },
        isExactUrl: true,
      },
      {
        id: 'invoice',
        label: t('content.invoice'),
        statusIcon: invoiceId ? StatusIcon.Completed : StatusIcon.Pending,
        disabled: !invoiceInfo,
        url: invoiceUrl,
        onClick: event => {
          if (!invoiceId) {
            // For a placeholder, the location state must be preserved
            event.preventDefault();
            void navigate(invoiceUrl, {
              state: location.state as InvoiceInfo,
            });
          }
        },
      },
      ...(invoiceId && invoiceRejectionNotes.length
        ? [
            {
              id: 'rejectionNotes',
              label: t('content.rejection-notes'),
              url: `/admin/invoices/${invoiceId}/rejection-notes`,
            },
          ]
        : []),
    ];
  }, [
    invoiceId,
    t,
    invoice,
    journeys,
    isFetchingJourneys,
    invoiceInfo,
    invoiceRejectionNotes,
    navigate,
    location.state,
  ]);

  return (
    <LeftPanelSectionTemplate
      title={
        invoice ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            {invoice.invoiceNumber}{' '}
            <StatusBadge status={invoice.status} small={!isMdUp} />
          </span>
        ) : (
          displayedInvoiceName || t('content.untitled')
        )
      }
      isLoading={
        isFetchingInvoice ||
        isFetchingJourneys ||
        isFetchingRejectionNotes ||
        approvalMutation.isPending ||
        rejectionMutation.isPending
      }
      actions={
        <InvoiceTemplateActions
          isLoading={isFetchingInvoice}
          isOperator={isOperator}
          invoiceStatus={invoice ? invoice.status : null}
          isSubmitEnabled={isSubmitEnabled}
          onSubmitNewInvoiceRequest={() => {
            submitNewInvoiceHandler.current?.();
          }}
          onApproveRequest={() => {
            approvalMutation.mutate();
          }}
          onRejectRequest={() => {
            setInvoiceIdToReject(invoiceId);
          }}
        />
      }
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-invoices')}
      goBackLinkUrl="/admin/invoices"
    >
      <Outlet context={contextValue} />
      <RequiredNoteConfirmationDialog
        open={Boolean(invoiceIdToReject)}
        title={t('content.reject-invoice')}
        noteLabel={t('content.rejection-note-reason')}
        onConfirmRequest={note => {
          rejectionMutation.mutate(note);
        }}
        onCloseRequest={() => {
          if (!rejectionMutation.isPending) {
            setInvoiceIdToReject(null);
          }
        }}
        disabled={rejectionMutation.isPending}
        loading={rejectionMutation.isPending}
      />
    </LeftPanelSectionTemplate>
  );
};

export default InvoiceTemplate;

export const InvoiceSubPageWrapper = () => (
  <OutletContextReliantRoute<InvoiceTemplateContext>
    getOutletContextState={({
      invoice,
      invoiceId,
      isFetching,
      invoiceInfo,
      journeysManagement: { journeys, isLoading: isLoadingJourneys },
    }) =>
      /* It will fail if, after all fetching has finished, there is no minimal invoice info, or any empty placeholder still remains, or no journeys and invoice is not Rejected  */
      invoiceInfo &&
      (invoice ||
        (!isLoadingJourneys && !journeys.find(({ id }) => id == null)))
        ? 'ready'
        : (invoiceId && isFetching) || isLoadingJourneys
          ? 'pending'
          : 'error'
    }
    errorRedirectionPath="/admin/invoices"
  />
);
